import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import type { Assignee, WoPriority, WoStatus } from '../dummy'
import { PRIORITY_META } from '../dummy'
import { useStatusMeta } from '../moduleContext'
import { UserAvatar } from '../../../components/UserAvatar'

// ─── Status dot ──────────────────────────────────────────────────────────────

export function StatusDot({ status }: { status: WoStatus }) {
  // Reads the WorkModuleProvider context so the terminal-success
  // status renders as "Completed" in the Tasks module and stays
  // "Resolved" in Work Orders.
  const m = useStatusMeta(status)
  return (
    <Stack direction="row" alignItems="center" spacing={0.85}>
      <Box
        sx={{
          width: 8, height: 8, borderRadius: 99, bgcolor: m.color,
          boxShadow: status === 'in_progress' ? `0 0 0 3px ${m.color}22` : 'none',
        }}
      />
      <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.secondary' }}>{m.label}</Typography>
    </Stack>
  )
}

// ─── Priority chip ───────────────────────────────────────────────────────────

export function PriorityChip({ priority }: { priority: WoPriority }) {
  const m = PRIORITY_META[priority] ?? { label: priority, color: '#64748B', bg: '#F8FAFC' }
  return (
    <Chip label={m.label} size="small"
      sx={{ bgcolor: m.bg, color: m.color, fontWeight: 700, height: 20, fontSize: 11, '& .MuiChip-label': { px: 0.85 } }} />
  )
}

// ─── Assignee avatars ────────────────────────────────────────────────────────

export function AssigneeAvatars({ assignees, max = 3 }: { assignees: Assignee[]; max?: number }) {
  if (assignees.length === 0) {
    return <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>Unassigned</Typography>
  }
  return (
    <Stack direction="row" spacing={-0.75}>
      {assignees.slice(0, max).map((a) => (
        <Tooltip key={a.id} title={a.name} arrow>
          <Box sx={{ display: 'inline-flex', borderRadius: 99,
            border: '2px solid', borderColor: 'background.paper' }}>
            <UserAvatar name={a.name} avatarUrl={a.avatarUrl} size={22}
              sx={{ bgcolor: a.color, fontSize: 9, fontWeight: 700, cursor: 'default' }} />
          </Box>
        </Tooltip>
      ))}
      {assignees.length > max && (
        <Box sx={{ width: 22, height: 22, borderRadius: 99, bgcolor: 'action.hover', color: 'text.secondary', border: '2px solid', borderColor: 'background.paper',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
          +{assignees.length - max}
        </Box>
      )}
    </Stack>
  )
}
